import { createAsyncThunk } from '@reduxjs/toolkit'
import { instance } from '../api/api'
import { ClaimResponseType, ClaimsFromPlacesResponseType, CommentsType } from './types'

export const getAllClaims = createAsyncThunk(
  'claims/getAllClaims',
  async (
    {
      yearsAndMonth,
      token,
      search,
      status,
      places,
      type,
      segment,
    }: {
      yearsAndMonth?: string
      token: string
      search?: string
      status?: string
      places?: string
      type?: string
      segment?: string
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.get<ClaimResponseType[]>(
        yearsAndMonth ? `place-requests?yearAndMonth=${yearsAndMonth}` : 'place-requests',
        {
          params: {
            ...(search ? { search: search } : {}),
            ...(status ? { status: status } : {}),
            ...(places ? { places: places } : {}),
            ...(segment ? { segment: segment } : {}),
            ...(type ? { type: type } : {}),
          },
          headers: { authorization: `Bearer ${token}` },
        },
      )

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const getStatsClaims = createAsyncThunk(
  'claims/getStatsClaims',
  async (
    {
      yearsAndMonth,
      token,
      search,
      status,
      place,
      type,
      segment,
      page = 1,
      limit = 1000,
      dateRange,
      companyId = null,
      paid,
      categoryNames,
      userId,
      establishmentId,
      clientId,
    }: {
      yearsAndMonth?: string
      token: string
      search?: string
      status?: string | string[]
      place?: string
      type?: string
      segment?: string
      page?: number
      limit?: number
      dateRange?: { start?: string; end?: string }
      companyId?: number | null
      paid?: boolean
      categoryNames?: string[]
      userId?: string
      establishmentId?: number
      clientId?: number
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.get<[ClaimResponseType[], number]>(
        yearsAndMonth
          ? `place-requests/paginated?yearAndMonth=${yearsAndMonth}`
          : 'place-requests/paginated',
        {
          params: {
            ...(search !== undefined ? { search } : {}),
            ...(status !== undefined ? { status } : {}),
            ...(place !== undefined ? { place } : {}),
            ...(type !== undefined ? { type } : {}),
            ...(segment !== undefined ? { segment } : {}),
            ...(companyId !== undefined ? { companyId } : {}),
            ...(dateRange?.start ? { start: dateRange.start } : {}),
            ...(dateRange?.end ? { end: dateRange.end } : {}),
            ...(paid !== undefined ? { paid } : {}),
            ...(categoryNames !== undefined ? { categoryNames } : {}),
            ...(userId !== undefined ? { userId } : {}),
            ...(establishmentId !== undefined ? { establishmentId } : {}),
            ...(clientId !== undefined ? { clientId } : {}),
            page,
            limit,
          },
          headers: { authorization: `Bearer ${token}` },
        },
      )

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const getPagedClaims = createAsyncThunk(
  'claims/getPagedClaims',
  async (
    {
      yearsAndMonth,
      token,
      search,
      status,
      place,
      type,
      segment,
      page = 1,
      limit = 10,
      dateRange,
      companyId = null,
      paid,
      categoryNames,
      userId,
      establishmentId,
      clientId,
    }: {
      yearsAndMonth?: string
      token: string
      search?: string
      status?: string | string[]
      place?: string
      type?: string
      segment?: string
      page?: number
      limit?: number
      dateRange?: { start?: string; end?: string }
      companyId?: number | null
      paid?: boolean
      categoryNames?: string[]
      userId?: string
      establishmentId?: number
      clientId?: number
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.get<ClaimResponseType[]>(
        yearsAndMonth
          ? `place-requests/paginated?yearAndMonth=${yearsAndMonth}`
          : 'place-requests/paginated',
        {
          params: {
            ...(search !== undefined ? { search } : {}),
            ...(status !== undefined ? { status } : {}),
            ...(place !== undefined ? { place } : {}),
            ...(type !== undefined ? { type } : {}),
            ...(segment !== undefined ? { segment } : {}),
            ...(companyId !== undefined ? { companyId } : {}),
            ...(dateRange?.start ? { start: dateRange.start } : {}),
            ...(dateRange?.end ? { end: dateRange.end } : {}),
            ...(paid !== undefined ? { paid } : {}),
            ...(categoryNames !== undefined ? { categoryNames } : {}),
            ...(userId !== undefined ? { userId } : {}),
            ...(establishmentId !== undefined ? { establishmentId } : {}),
            ...(clientId !== undefined ? { clientId } : {}),
            page,
            limit,
          },
          headers: { authorization: `Bearer ${token}` },
        },
      )

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const getClaimsForDownload = createAsyncThunk(
  'claims/getClaimsForDownload',
  async (
    {
      yearsAndMonth,
      token,
      search,
      status,
      place,
      type,
      segment,
      page = 1,
      limit = 10,
      dateRange,
      company,
      paid,
      categoryNames,
    }: {
      yearsAndMonth?: string
      token: string
      search?: string
      status?: string | string[]
      place?: string
      type?: string
      segment?: string
      page?: number
      limit?: number
      dateRange?: { start?: string; end?: string }
      company?: number
      paid?: boolean
      categoryNames?: string[]
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.get<ClaimResponseType[]>(
        yearsAndMonth
          ? `place-requests/paginated?yearAndMonth=${yearsAndMonth}`
          : 'place-requests/paginated',
        {
          params: {
            ...(search !== undefined ? { search } : {}),
            ...(status !== undefined ? { status } : {}),
            ...(place !== undefined ? { place } : {}),
            ...(type !== undefined ? { type } : {}),
            ...(segment !== undefined ? { segment } : {}),
            ...(company !== undefined ? { company } : {}),
            ...(dateRange?.start ? { start: dateRange.start } : {}),
            ...(dateRange?.end ? { end: dateRange.end } : {}),
            ...(paid !== undefined ? { paid } : {}),
            ...(categoryNames !== undefined ? { categoryNames } : {}),
            page,
            limit,
          },
          headers: { authorization: `Bearer ${token}` },
        },
      )

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)
export const getAllClaimsFromPlatforms = createAsyncThunk(
  'claims/getAllClaimsFromPlatforms',
  async (
    {
      companyId,
      yearsAndMonth,
      token,
      search,
      status,
      places,
      type,
    }: {
      companyId?: string
      yearsAndMonth?: string
      token: string
      search?: string
      status?: string
      places?: string
      type?: string
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.get<ClaimsFromPlacesResponseType>(
        yearsAndMonth
          ? `place-requests/from-places?yearAndMonth=${yearsAndMonth}`
          : 'place-requests/from-places',
        {
          params: {
            ...(companyId ? { companyId: companyId } : {}),
            ...(search ? { search: search } : {}),
            ...(status ? { status: status } : {}),
            ...(places ? { places: places } : {}),
            ...(type ? { type: type } : {}),
          },
          headers: { authorization: `Bearer ${token}` },
        },
      )

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

//

export const getCurrentClaim = createAsyncThunk(
  'claims/getCurrentClaim',
  async ({ claimId, token }: { claimId: string | undefined; token: string }, thunkAPI) => {
    try {
      const response = await instance.get<ClaimResponseType>(`place-requests/${claimId}`, {
        headers: { authorization: `Bearer ${token}` },
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const getCurrentClaimFromCurrentPlatform = createAsyncThunk(
  'claims/getCurrentClaimFromCurrentPlatform',
  async (
    {
      placeId,
      yearsAndMonth,
      token,
      search,
      status,
    }: {
      placeId: string | undefined
      yearsAndMonth: string
      token: string
      search?: string
      status?: string
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.get<ClaimResponseType[]>(
        `place-requests/from-places?yearAndMonth=${yearsAndMonth}&places=${placeId}`,
        {
          params: {
            ...(search ? { search: search } : {}),
            ...(status ? { status: status } : {}),
          },
          headers: { authorization: `Bearer ${token}` },
        },
      )

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const createClaim = createAsyncThunk(
  'claims/createClaim',
  async (
    {
      token,
      endDate,
      startDate,
      userId,
      peopleCount,
      placeId,
      force,
      status,
      client,
      manager,
      serviceFeePercent,
      serviceFeeAmount,
      discountPercent,
      discountAmount,
      comment,
    }: {
      userId: string
      placeId: string
      peopleCount: string
      startDate: string
      endDate: string
      token: string
      force?: boolean
      status?: string
      client?: number
      manager?: number
      serviceFeePercent?: number
      serviceFeeAmount?: number
      discountPercent?: number
      discountAmount?: number
      comment?: string
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.post<ClaimResponseType>(
        'place-requests',
        {
          endDate,
          startDate,
          userId,
          peopleCount,
          placeId,
          force,
          status,
          client: Number(client),
          manager: Number(manager),
          serviceFeePercent,
          serviceFeeAmount,
          discountPercent,
          discountAmount,
          comment,
        },
        {
          headers: { authorization: `Bearer ${token}` },
        },
      )

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message)
    }
  },
)
export const createClaimPublic = createAsyncThunk(
  'claims/createClaimPublic',
  async (
    {
      endDate,
      startDate,
      peopleCount,
      placeId,
      publicClientData,
      products,
    }: {
      placeId: string
      peopleCount: string
      startDate: string
      endDate: string
      client?: number
      publicClientData?: any
      products?: any
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.post<ClaimResponseType>('place-requests-public', {
        endDate,
        startDate,
        products,
        peopleCount,
        placeId,
        publicClientData,
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message)
    }
  },
)

export const changeClaimMainInformation = createAsyncThunk(
  'claims/changeClaimMainInformation',
  async (
    {
      token,
      claimId,
      payload,
    }: {
      token: string
      claimId: string | undefined
      payload: any
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.patch<ClaimResponseType>(
        `place-requests/${claimId}`,
        payload,
        {
          headers: { authorization: `Bearer ${token}` },
        },
      )

      return response.data
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Произошла ошибка при изменении данных'
      return thunkAPI.rejectWithValue({
        errors: [errorMessage],
        fieldsErrors: undefined,
        message: errorMessage,
      })
    }
  },
)

export const changeClaimDescription = createAsyncThunk(
  'claims/changeClaimDescription',
  async (
    {
      token,
      claimId,
      description,
    }: {
      token: string
      claimId: string | undefined
      description: string
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.patch<ClaimResponseType>(
        `place-requests/${claimId}`,
        {
          description,
        },
        {
          headers: { authorization: `Bearer ${token}` },
        },
      )

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const deleteCurrentClaim = createAsyncThunk(
  'claims/deleteCurrentClaim',
  async ({ claimId, token }: { claimId: string | undefined; token: string }, thunkAPI) => {
    try {
      const response = await instance.delete<ClaimResponseType>(`place-requests/${claimId}`, {
        headers: { authorization: `Bearer ${token}` },
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const createClaimComments = createAsyncThunk(
  'claims/createClaimComments',
  async (
    {
      formData,
    }: {
      formData: any
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.post<CommentsType>('request-comments', formData)

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const getClaimComments = createAsyncThunk(
  'claims/getClaimComments',
  async (
    {
      requestId,
    }: {
      requestId: string | undefined
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.get<CommentsType[]>(`request-comments?requestId=${requestId}`)

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)
